import styled, { css } from "styled-components";
import type {
  CardBlockColumns,
  CardBlockVariant,
} from "@/services/api/types";

export const Card = styled.div<{ variant: CardBlockVariant }>`
  border-radius: 0.5rem;
  background: ${(props) =>
    props.variant === "gradient"
      ? props.theme.gradients.light2
      : props.theme.colors.lightest};
  padding: 1.8rem;
  ${(props) =>
    props.variant === "white" &&
    `
  border: 1px solid ${props.theme.colors.light};
  `}
  ${(props) =>
    props.variant === "transparent" &&
    `
  background: transparent;
  border: none;
  `}
  &&& h5 {
    text-transform: unset;
    margin-top: 0;
    font-size: 1.125rem;
    line-height: 1.25rem;
  }
`;

export const CardWrapper = styled.div<{ columns: CardBlockColumns }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  ${(props) => props.theme.media.medium} {
    ${(props) =>
      props.columns === "two" &&
      `
        grid-template-columns: 1fr 1fr;
    `}
    ${(props) =>
      props.columns === "three" &&
      `
        grid-template-columns: 1fr 1fr 1fr;
      `}
  }
  ${(props) =>
    props.columns === "auto" &&
    `
      display: flex;
      flex-wrap: wrap;
      ${Card} {
        flex-grow: 1;
        width: calc(50% - 1rem);
      }
    `}
`;

export const VideoWrapper = styled.div`
  width: 100%;
  padding-top: 56.25%;
  position: relative;
  overflow: hidden;
  margin-bottom: 1.5rem;
  .react-player {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

export type FrozenHeaderTableVariant = "grid" | "rowDividers";

const tableStyles = css<{ $variant: FrozenHeaderTableVariant }>`
  font-weight: 300;

  table {
    min-width: 100%;
    line-height: 1.25rem;
    table-layout: auto;

    &,
    th,
    td {
      border-color: ${(props) => props.theme.colors.utility};
    }

    th:not(:first-child),
    td:not(:first-child) {
      min-width: 12rem;
    }

    td,
    th:last-of-type,
    td:last-of-type {
      padding: 1rem 1.2rem !important;
    }

    img {
      max-width: 100%;
      height: auto;
      display: block;
    }

    ${(props) =>
      props.$variant === "rowDividers" &&
      css`
        border: 0;
        margin-top: 0.2rem;

        tbody tr,
        tbody tr:nth-of-type(2n + 1) {
          background: ${props.theme.colors.lightest};
        }

        th,
        td {
          border: 0 !important;
          border-bottom: 1px solid ${props.theme.colors.light} !important;
        }
      `}
  }
`;

export const StickyHeaderOverlay = styled.div<{
  $variant: FrozenHeaderTableVariant;
}>`
  position: fixed;
  overflow: hidden;
  z-index: 5;
  visibility: hidden;
  pointer-events: none;
  ${tableStyles}
  table {
    margin: 0;
  }
  tr {
    background: ${(props) =>
      props.$variant === "rowDividers"
        ? props.theme.colors.lightest
        : props.theme.gradients.light2};
  }
`;

export const DatoTable = styled.div<{
  $variant: FrozenHeaderTableVariant;
}>`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  ${tableStyles}

  li {
    line-height: 1.5rem;
  }
  ul {
    margin: 0;
  }
  li > ul,
  li > ol {
    margin-top: 0.5rem;
  }
  li + li {
    margin-top: 0.5rem;
  }
  li > ul li + li,
  li > ol li + li {
    margin-top: 0.5rem;
  }
  p {
    margin: 0 0 0.5rem;
    font-size: 0.875rem;
  }
`;

export const FloatingCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  ${(props) => props.theme.media.medium} {
    align-items: center;
    flex-direction: row;
  }
`;

export const FloatingCard = styled.div`
  background: ${(props) => props.theme.colors.accent1};
  color: ${(props) => props.theme.colors.lightest};
  border-radius: 0.5rem;
  width: 200px;
  flex-shrink: 0;
  padding: 1rem;
  margin: 1rem 0 1rem 1rem;
  p {
    margin: 0;
  }
`;

export const FloatingCardText = styled.div`
  flex: 1;
`;

export const ImageWithModal = styled.div<{ centered?: boolean }>`
  width: fit-content;
  margin: 1rem 0;
  ${(props) =>
    props.centered &&
    `
  margin-left: auto;
  margin-right: auto;
  `}
`;

export const ImageWrapper = styled.div`
  position: relative;
`;

export const InlineImage = styled.img<{ centered?: boolean }>`
  position: relative;
  max-height: 350px;
  max-width: 100%;
  object-fit: cover;
  cursor: pointer;
  display: block;
  ${(props) =>
    props.centered &&
    `
  margin-left: auto;
  margin-right: auto;
  `}
`;

export const HoverImage = styled.div`
  position: absolute;
  cursor: pointer;
  display: none;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.25);
  align-items: center;
  justify-content: center;
  ${ImageWrapper}:hover & {
    display: flex;
  }
`;

export const ClickToEnlarge = styled.div`
  cursor: pointer;
  padding-top: 0.3rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  svg {
    height: 20px;
    margin-right: 0.2rem;
  }
`;

export const ModalImage = styled.img`
  width: 100%;
  max-height: 100%;
  object-fit: cover;
  padding-top: 0.5rem;

`;

export const Diagram = styled(InlineImage)`
  cursor: auto;
  margin: 1rem 0;
`;
